import './administrationPermissionView.scss'
import { useContext, useEffect, useState } from "react";
import FormField from "../../../../components/formField/formField";
import { FormFieldProps } from "../../../../components/formField/types/formFieldProps";
import { ReactComponent as Search } from "../../../../icons/search.svg";
import { apiContext } from '../../../../contexts/apiContext';
import UseCaseOptionField from './useCaseOptionField/useCaseOptionField';
import { Option } from '../../../../components/shared/selectInput/types/selectInputProps';
import ButtonField from '../../../../components/buttonField/buttonField';
import { classNameBtn } from '../../../../components/shared/button/types/buttonProps';
import { ButtonFieldProps } from '../../../../components/buttonField/types/buttonFieldProps';
import { stylizationVerticalAlign } from '../../../../components/shared/stylization';
import { notifyError, notifySuccess } from '../../../../components/snackbar/snackbar';
import Spinner from '../../../../components/spinner/spinner';
import RolesToPermissions from '../../../../helpers/rolesToPermissions';
import PermissionsContext, { PortalGroup, RoleDefinition, UseCase, UseCaseDefinitions, editableUseCasePermissions } from '../../../../contexts/permissionsContext';
import { getRoleDefinitions, saveRoleDefinitions } from '../../../../services/administrationService/administrationService';
import AuthContext from '../../../../contexts/authContext';
import { onlineOfflinePermissionsCheck } from '../../../../helpers/permissionsHelper';
import { setUserPermissionsIdb } from '../../../../database/indexedDbReadWrite';

const AdministrationPermissionView = (props: any) => {
    const api = useContext(apiContext);
    const { setFunctionsPermissions, extendedUserPermissions } = useContext(PermissionsContext);
    const { userRoles, username } = useContext(AuthContext);
    const [search, setSearch] = useState<string>();
    const [loading, setLoading] = useState<boolean>(false);
    const [disable, setDisable] = useState<boolean>(true);
    const [useCasesDict, setUseCaseDict] = useState<UseCase[]>();
    const [portalGroupsDict, setPortalGroupsDict] = useState<PortalGroup[]>([]);
    const [portalGroupsDictOptions, setPortalGroupsDictOptions] = useState<Option[]>([]);
    const [roleDefinitionsDict, setRoleDefinitionsDict] = useState<RoleDefinition[]>();
    const [currentUseCaseDefinitions, setCurrentUseCaseDefinitions] = useState<UseCaseDefinitions[]>([]);
    const [newUseCaseDefinitions, setNewUseCaseDefinitions] = useState<UseCaseDefinitions[]>([]);

    // Use cases list
    useEffect(() => {
        setLoading(true);
        getRoleDefinitions(api.apiUrl + api.apiVersion)
        .then((res: any) => {
            setUseCaseDict(res.useCases.filter((item: any) => editableUseCasePermissions.includes(item.koD_PU)));
            setRoleDefinitionsDict(res.roleDefinitions);
            setPortalGroupsDict(res.portalGroups);
            preparePortalGroupsSelectOptions(res.portalGroups);
            setLoading(false);
        })
        .catch((error: any) => {
            setLoading(false);
            notifyError({ message: "Próba pobrania danych zakończona niepowodzeniem" })
        })

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (useCasesDict && portalGroupsDict && roleDefinitionsDict) prepareUseCaseDefinitions();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [useCasesDict, portalGroupsDict, roleDefinitionsDict])

    const prepareUseCaseDefinitions = () => {
        var useCaseDefinitions: UseCaseDefinitions[] = [];
        useCasesDict?.forEach(item => {
            var def: UseCaseDefinitions = { useCase: item, portalGroups: [] };
            var portalGroupIds: number[] = [];
            portalGroupsDict.forEach(elem => {
                if (roleDefinitionsDict?.some(x => x.portaL_GRUPA_ID === elem.id && x.przypadeK_UZYCIA_ID === item.id)) {
                    portalGroupIds.push(elem.id);
                }
            });
            portalGroupIds.forEach(elem => def.portalGroups.push(portalGroupsDict.find(x => x.id === elem)!));
            useCaseDefinitions.push(def);
        });
        setCurrentUseCaseDefinitions(useCaseDefinitions);

        var useCaseDefinitionsCopy = useCaseDefinitions.map(obj => ({ ...obj }));
        setNewUseCaseDefinitions(useCaseDefinitionsCopy);
    }

    const preparePortalGroupsSelectOptions = (portalGroups: PortalGroup[]) => {
        var portalGroupsOptions: Option[] = portalGroups.map(item => {
            return { name: item.nazwa, value: item.id };
        });
        setPortalGroupsDictOptions(portalGroupsOptions);
    }

    const handleNewUseCaseDefinitionsChange = (newData: UseCaseDefinitions[]) => {
        setNewUseCaseDefinitions(newData);
        setDisable(!checkIfModified());
    }

    // Search input
    const onChangeInput = (event: string) => {
        setSearch(event);
    }

    const searchInputProps: FormFieldProps = {
        labelName: "Znajdź przypadek użycia",
        inputPlaceholder: "Szukaj",
        value: search,
        className: "searchDictsInput",
        type: "text",
        additionalIcon: Search,
        pristine: true,
        keepPristine: true,
        pristineIcon: true,
        setValue: onChangeInput
    }

    // Save button
    const handleSaveBtnClick = () => {
        var onOffCheck = onlineOfflinePermissionsCheck(newUseCaseDefinitions);
        if (onOffCheck) {
            setLoading(true);
            saveRoleDefinitions(api.apiUrl + api.apiVersion, newUseCaseDefinitions)
            .then(() => {
                notifySuccess({ message: "Zapisano definicje uprawnień" });
                setDisable(true);
                RolesToPermissions(userRoles, api.apiUrl + api.apiVersion)
                .then((res: any) => {
                    let definitions = res.definitions;
                    if (definitions.roleDefinitions !== null) {
                        setRoleDefinitionsDict(definitions.roleDefinitions);
                    } else {
                        setCurrentUseCaseDefinitions(JSON.parse(JSON.stringify(newUseCaseDefinitions)));
                    }

                    setLoading(false);
                    setFunctionsPermissions(res.permissions);
                    setUserPermissionsIdb({ name: username, roles: userRoles, permissions: res.permissions, extended: extendedUserPermissions });
                })
                .catch(() => {
                    setLoading(false);
                });
            })
            .catch(() => {
                setLoading(false);
                notifyError({ message: "Próba zapisania definicji uprawnień zakończona niepowodzeniem" });
            })
        } else {
            notifyError({ message: "Uprawnienia dla przejścia do trybu ONLINE i OFFLINE nie są zgodne - brak możliwości zapisania definicji uprawnień" });
        }
    }

    const checkIfModified = () => {
        return JSON.stringify(currentUseCaseDefinitions) !== JSON.stringify(newUseCaseDefinitions);
    }

    const saveBtnProps: ButtonFieldProps = {
        BtnProps: {
            onClick: handleSaveBtnClick,
            style: {
                width: '180px',
                height: '45px',
            },
            className: `${classNameBtn.Small} body-txt-medium`,
            disabled: disable
        },
        placeHolder: "Zapisz zmiany",
        placeHolderStyle: stylizationVerticalAlign
    }

    return (
        <>
            {loading && <div className="tableSpinner"><Spinner /></div>}
            {!loading &&
                <>
                    <div className="searchUseCasesWrapper">
                        <FormField {...searchInputProps} />
                    </div>
                    <div className='useCasesOptionsWrapper'>
                        {newUseCaseDefinitions?.filter(item => {
                            if (search !== undefined && search !== "") {
                                return item.useCase.koD_PU?.toLowerCase().includes(search.toLowerCase()) ||
                                    item.useCase.nazwA_PU?.toLowerCase().includes(search.toLowerCase())
                            }
                            return true;
                        })?.map(item => {
                            return (
                                <UseCaseOptionField
                                    key={item.useCase.id}
                                    rolesSelectOptions={portalGroupsDictOptions}
                                    useCaseDefinitions={item}
                                    newUseCasesDefinitions={newUseCaseDefinitions}
                                    setNewUseCasesDefinitions={handleNewUseCaseDefinitionsChange}
                                />
                            )
                        })}
                    </div>
                    <div className='saveBtnWrapper'>
                        <ButtonField {...saveBtnProps} />
                    </div>
                </>
            }
        </>
    )
}

export default AdministrationPermissionView;